// Iter125 · P1 Fase 3b · Configuración Operativa del ítem del catálogo.
// Componente controlado reutilizable para Catálogo, Servicios Rápidos y Menús.
//
// Persiste dentro del subdocumento `configuracion_operativa`:
//   { genera_tarea, nombre_tarea, area, momento, responsable_sugerido,
//     prioridad, instrucciones }
//
// Al desactivar `genera_tarea`, la configuración deja de aplicar (Orden
// Maestra tratará al ítem como "Requiere configuración") pero los valores
// permanecen guardados para volver a activarla sin volver a capturar.
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const emptyConfig = {
  genera_tarea: false,
  nombre_tarea: "",
  area: "",
  momento: "",
  responsable_sugerido: "",
  prioridad: "media",
  instrucciones: "",
};

const prioridades = [
  { value: "alta", label: "Alta" },
  { value: "media", label: "Media" },
  { value: "baja", label: "Baja" },
];

export const configuracionOperativaEmpty = { ...emptyConfig };

export const ConfiguracionOperativaFields = ({ value, onChange, testidPrefix = "config-op" }) => {
  const v = { ...emptyConfig, ...(value || {}) };
  const patch = (partial) => onChange({ ...v, ...partial });
  const limpiar = () => onChange({ ...emptyConfig });

  return (
    <div className="border-t border-stone-200 pt-3 space-y-3" data-testid={`${testidPrefix}-section`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-stone-700">Configuración operativa</p>
          <p className="text-xs text-stone-500">
            Define la tarea que Orden Maestra generará cuando este ítem sea contratado.
            Si está desactivado, el ítem aparecerá como <span className="italic">Requiere configuración</span> en el evento.
          </p>
        </div>
        <button
          type="button"
          onClick={limpiar}
          className="text-xs text-stone-500 hover:text-red-600 underline"
          data-testid={`${testidPrefix}-limpiar`}
        >Desactivar y limpiar</button>
      </div>

      <div className="flex items-center justify-between p-2 hover:bg-stone-50 rounded">
        <Label className="flex-1 cursor-pointer text-sm">Genera tarea automática al contratarse</Label>
        <Switch
          checked={!!v.genera_tarea}
          onCheckedChange={(c) => patch({ genera_tarea: c })}
          data-testid={`${testidPrefix}-genera-tarea`}
        />
      </div>

      {v.genera_tarea && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label>Nombre de la tarea *</Label>
            <Input
              value={v.nombre_tarea}
              onChange={(e) => patch({ nombre_tarea: e.target.value })}
              placeholder="Ej: Confirmar llegada y montaje de DJ"
              data-testid={`${testidPrefix}-nombre-tarea`}
            />
          </div>
          <div>
            <Label>Área responsable</Label>
            <Input
              value={v.area}
              onChange={(e) => patch({ area: e.target.value })}
              placeholder="Ej: Proveedores, Cocina, Montaje..."
              data-testid={`${testidPrefix}-area`}
            />
          </div>
          <div>
            <Label>Prioridad</Label>
            <Select value={v.prioridad || "media"} onValueChange={(p) => patch({ prioridad: p })}>
              <SelectTrigger data-testid={`${testidPrefix}-prioridad`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {prioridades.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Momento (hora HH:MM o desfase en minutos)</Label>
            <Input
              value={v.momento}
              onChange={(e) => patch({ momento: e.target.value })}
              placeholder="Ej: 18:00 o -120 (2h antes del inicio)"
              data-testid={`${testidPrefix}-momento`}
            />
          </div>
          <div>
            <Label>Responsable sugerido (opcional)</Label>
            <Input
              value={v.responsable_sugerido}
              onChange={(e) => patch({ responsable_sugerido: e.target.value })}
              placeholder="Ej: Coordinador de piso"
              data-testid={`${testidPrefix}-responsable`}
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Instrucciones operativas</Label>
            <Textarea
              rows={2}
              value={v.instrucciones}
              onChange={(e) => patch({ instrucciones: e.target.value })}
              placeholder="Notas internas para quien ejecute la tarea"
              data-testid={`${testidPrefix}-instrucciones`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfiguracionOperativaFields;
